import {
  composeBranchedPayloadTemplate,
  composeBranchedTemplate,
  declaredBranches,
  templateOwnedIds,
  type TemplateOwnedIds,
} from '@bmo/form-core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { WorkflowTemplate } from '@/api/workflows';

/** Loads one workflow by id. Injected rather than imported — see {@link useFormBranch}. */
export type BranchTemplateLoader = (templateId: string) => Promise<WorkflowTemplate>;

export type FormBranchHost = {
  /** What the user navigates: the base prefix, then the target's steps. Hand to
   *  `FormShell`'s `template`. Null only while the base template itself is still loading. */
  effectiveTemplate: WorkflowTemplate | null;
  /** What ships to BMO — the target's own steps under the base form's identity. Undefined
   *  off the branch path, which is what every non-branching surface already passes. */
  payloadTemplate: WorkflowTemplate | undefined;
  /** Which branch is composed in, and which ids it owns. Null = the base form. */
  formBranch: { targetTemplateId: string; owned: TemplateOwnedIds } | null;
  /** Hand to `FormShell`'s `onFormBranchChange`. Its PRESENCE is what enables branching in
   *  the shell at all, so a host that wires this hook opts the whole mechanism in. */
  onFormBranchChange: (targetTemplateId: string | null) => void;
  /** A target is being fetched right now. Hosts show this without unmounting the shell —
   *  a page-level spinner here would take every answer with it. */
  loading: boolean;
  /** The selected target could not be loaded, so we are still on the base form. See the
   *  catch in the fetch effect for why this is surfaced rather than swallowed. */
  loadFailed: boolean;
  /**
   * The composed branch's target id, or null. A ref because the advisor's save/submit run
   * inside a chain the user started and a captured render value would be the one from
   * before that chain's earlier links changed — the same reason `metadataRef` exists.
   *
   * Mirrors the COMPOSED branch, not the requested one: a target that failed to load is not
   * a form anyone is filling, and naming it would make BMO record a swap never shown.
   */
  activeTargetIdRef: React.MutableRefObject<string | null>;
  /**
   * Select a branch whose template the caller already holds — no fetch, no flicker.
   *
   * Exists for the advisor's resume path, which has to load the target itself: a submitted
   * application is pinned to the version it was filed on, and only the caller knows which
   * label to ask for.
   */
  adoptBranch: (target: WorkflowTemplate) => void;
};

/**
 * The host half of form branching: owns fetching the selected form and composing it onto
 * the base one.
 *
 * `FormShell` reports that the answers now select a different form and nothing more — it
 * does not fetch templates, and the host owns which `template` prop it hands back down. See
 * `FormShell`'s `onFormBranchChange` / `formBranch` docs for that contract, and
 * `@bmo/form-core/composeBranchedTemplate` for the identity-from-base / content-from-target
 * split the composition applies.
 *
 * <h3>Why `loadTemplate` is injected</h3>
 * The surfaces that branch do not share an API client. The advisor and both previews use the
 * authenticated `workflowsApi.get`; the public customer runtime uses the anonymous
 * `publicWorkflowsApi.get`, and an ESLint `no-restricted-imports` rule on `src/public/**`
 * makes importing the authenticated one there a build failure. Taking the loader as an
 * argument is what lets one hook serve both without weakening that guard.
 *
 * <h3>The caller must NOT change the shell's `key`</h3>
 * A remount would discard every answer given, including the one that selected the branch —
 * which would immediately un-select it again. Hosts pass the composed template and hold the
 * key fixed.
 */
export function useFormBranch({
  template,
  loadTemplate,
}: {
  /** The BASE workflow, exactly as fetched. Never the composed one — that is the output. */
  template: WorkflowTemplate | null;
  loadTemplate: BranchTemplateLoader;
}): FormBranchHost {
  const [branchTargetId, setBranchTargetId] = useState<string | null>(null);
  const [branchTemplate, setBranchTemplate] = useState<WorkflowTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  // Templates already fetched this session. Someone who flips a radio back and forth is the
  // ordinary case, not an edge one, and refetching on each flip would stall the form behind a
  // network round trip every time.
  const cacheRef = useRef<Map<string, WorkflowTemplate>>(new Map());
  // Held in a ref so the fetch effect can stay keyed on `branchTargetId` ALONE. Call sites
  // routinely pass an inline arrow (`(id) => publicWorkflowsApi.get(id)`), which is a fresh
  // function every render — as a dependency it would refetch the target on every keystroke.
  const loadRef = useRef(loadTemplate);
  loadRef.current = loadTemplate;

  useEffect(() => {
    if (!branchTargetId) {
      setBranchTemplate(null);
      setLoadFailed(false);
      return;
    }
    const cached = cacheRef.current.get(branchTargetId);
    if (cached) {
      setBranchTemplate(cached);
      setLoadFailed(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    void (async () => {
      try {
        const loaded = await loadRef.current(branchTargetId);
        if (cancelled) return;
        cacheRef.current.set(branchTargetId, loaded);
        setBranchTemplate(loaded);
      } catch {
        // The target is unpublished, archived, gated out or gone. Staying on the base form is
        // the only safe answer — it keeps every answer already given and leaves the rule
        // visibly inert, rather than stranding anyone on a half-composed workflow. Not a page
        // error: the base form is still perfectly fillable.
        //
        // Surfaced via `loadFailed` rather than swallowed. A silent fallback here is
        // indistinguishable from "branching is not wired on this surface" and from "the rule
        // never reached the client", which is most of what makes a misconfigured branch so
        // hard to diagnose.
        if (cancelled) return;
        setBranchTemplate(null);
        setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [branchTargetId]);

  const adoptBranch = useCallback((target: WorkflowTemplate) => {
    cacheRef.current.set(target.id, target);
    setBranchTargetId(target.id);
  }, []);

  // Where the rule that selected this target sits on the BASE form — the seam the composition
  // splits on. Read from the base template's own steps, so it is the same index FormShell
  // resolved the branch at (the composed prefix is `base.steps.slice(0, i)`).
  const branchPoint = useMemo(
    () =>
      template && branchTargetId
        ? (declaredBranches(template.steps).find(
            (d) => d.branch.targetTemplateId === branchTargetId,
          ) ?? null)
        : null,
    [template, branchTargetId],
  );
  // Memoized rather than built inline: it is the dependency of all three memos below, and a
  // fresh object every render would make each of them recompute every render — which for
  // `composeBranchedTemplate` means a new `steps` array, and so a new `allSteps` inside
  // FormShell, on every keystroke.
  const branched = useMemo(
    () => (branchTemplate && branchPoint ? { branchTemplate, branchPoint } : null),
    [branchTemplate, branchPoint],
  );

  const effectiveTemplate = useMemo(() => {
    if (!template) return null;
    if (!branched) return template;
    return composeBranchedTemplate(
      template,
      branched.branchTemplate,
      branched.branchPoint.stepIndex,
      branched.branchPoint.branch.appendFirstStep,
    );
  }, [template, branched]);

  const payloadTemplate = useMemo(
    () =>
      template && branched
        ? composeBranchedPayloadTemplate(template, branched.branchTemplate)
        : undefined,
    [template, branched],
  );

  const formBranch = useMemo(
    () =>
      branched
        ? {
            targetTemplateId: branched.branchTemplate.id,
            owned: templateOwnedIds(branched.branchTemplate),
          }
        : null,
    [branched],
  );

  const activeTargetIdRef = useRef<string | null>(null);
  activeTargetIdRef.current = branched?.branchTemplate.id ?? null;

  return {
    effectiveTemplate,
    payloadTemplate,
    formBranch,
    onFormBranchChange: setBranchTargetId,
    loading,
    loadFailed,
    activeTargetIdRef,
    adoptBranch,
  };
}
